import { motion } from 'framer-motion';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image - Broadcast control room style */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_7622ca65-87c5-425f-ac54-f38f9958c6c6/artifacts/hys9v9oj_close-up-view-of-radio-mixing-desk-with-profession-2026-01-08-23-44-35-utc.jpg"
          alt="Studio Background"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
      </div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 md:px-12 text-center pt-16 sm:pt-20">
        {/* Main Logo - Responsive sizing with pulsing glow effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-2 sm:mb-4 relative"
        >
          {/* Continuous circular pulsing glow overlay - soft edges */}
          <motion.div
            className="absolute inset-0 z-5 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 45%, rgba(0, 240, 255, 0.12) 0%, rgba(255, 0, 153, 0.08) 20%, rgba(251, 191, 36, 0.1) 40%, transparent 65%)',
              filter: 'blur(20px)',
            }}
            animate={{
              opacity: [0.5, 0.9, 0.6, 1, 0.5],
              scale: [1, 1.05, 1, 1.08, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Secondary outer glow pulse */}
          <motion.div
            className="absolute inset-0 z-5 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 45%, rgba(251, 191, 36, 0.08) 0%, rgba(0, 240, 255, 0.05) 30%, transparent 55%)',
              filter: 'blur(40px)',
            }}
            animate={{
              opacity: [0.3, 0.7, 0.4, 0.8, 0.3],
              scale: [1.1, 1.15, 1.1, 1.2, 1.1],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />
          
          {/* Logo image */}
          <motion.img 
            src="https://customer-assets.emergentagent.com/job_7622ca65-87c5-425f-ac54-f38f9958c6c6/artifacts/zd4sl0r2_Logo%202.png"
            alt="Kingdom V.I.B.E. Studios" 
            className="h-48 sm:h-64 md:h-80 lg:h-[26rem] xl:h-[30rem] mx-auto relative z-10"
            style={{
              filter: 'saturate(1.3) contrast(1.1) brightness(1.1)',
            }}
            animate={{
              filter: [
                'saturate(1.3) contrast(1.1) brightness(1.1)',
                'saturate(1.4) contrast(1.15) brightness(1.15)',
                'saturate(1.3) contrast(1.1) brightness(1.1)',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            data-testid="hero-logo"
          />
        </motion.div>

        {/* Main Headline - "Powerful" and "Production" in gold/amber with faint glow */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black uppercase mb-4 sm:mb-6 md:mb-8"
          style={{ letterSpacing: '0.03em' }}
          data-testid="hero-headline"
        >
          <span className="text-white">Your Message Is </span>
          <span 
            className="text-amber-400"
            style={{ 
              textShadow: '0 0 20px rgba(251, 191, 36, 0.4), 0 0 40px rgba(251, 191, 36, 0.2)'
            }}
          >Powerful</span>
          <br />
          <span className="text-white">Your </span>
          <span 
            className="text-amber-400"
            style={{ 
              textShadow: '0 0 20px rgba(251, 191, 36, 0.4), 0 0 40px rgba(251, 191, 36, 0.2)'
            }}
          >Production</span>
          <span className="text-white"> Should Be Too</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-10 px-2"
          data-testid="hero-subheadline"
        >
          <p className="text-sm sm:text-base md:text-lg text-white/80 leading-relaxed">
            We build and manage production systems so faith-based leaders and creators can deliver their message with excellence and consistency.
          </p>
        </motion.div>

        {/* CTA Buttons - Slanted style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6 pb-8 sm:pb-0"
        >
          <a
            href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-neon btn-neon-gold text-sm sm:text-base px-4 sm:px-6 md:px-8 py-3 sm:py-4 w-full sm:w-auto"
            data-testid="hero-cta-primary"
          >
            <span>Schedule a Discovery Call</span>
          </a>
          <a
            href="#services"
            className="btn-neon btn-neon-gold text-sm sm:text-base px-4 sm:px-6 md:px-8 py-3 sm:py-4 w-full sm:w-auto"
            data-testid="hero-cta-secondary"
          >
            <span>Explore Our Services</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
